import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import '../styles/ContractForm.css';
import { graphqlRequest } from '../utils/graphqlRequest';
import { mergeTermRows, dropServiceTerms, rowsFromClientTerms } from './ProfilePages/components/quoteCatalog';
import QuoteTermsEditor from './ProfilePages/components/QuoteTermsEditor';
import ProposalServices, {
  lineFromPick,
  serviceTaskTotal,
  serviceTypeFromLines,
} from './Proposals/components/ProposalServices';
import { formatProposalTerms, rowsFromProposalTerms } from './Proposals/utils/proposalUtils';
import {
  FiAlignLeft,
  FiBriefcase,
  FiCalendar,
  FiDownload,
  FiEdit3,
  FiFileText,
  FiLink,
  FiPaperclip,
  FiTool,
  FiUploadCloud,
  FiUsers,
  FiX,
} from 'react-icons/fi';

const FIND_LEADS = `
  query FindLeadsForContract($page: Float!, $limit: Float!) {
    findAllBooking(page: $page, limit: $limit, includeSignUrls: false) {
      data {
        id
        fullName
        email
        phone
        address
        bookingFrequency
      }
    }
  }
`;

const FIND_ONE_LEAD = `
  query FindOneLeadForContract($id: String!) {
    findOneBooking(id: $id, includeFileUrls: false) {
      id
      fullName
      email
      phone
      address
      bookingFrequency
    }
  }
`;

const FIND_BOOKING_QUOTATIONS = `
  query FindBookingQuotationsForContract($bookingId: String!) {
    findBookingQuotation(bookingId: $bookingId) {
      id
      quotationNumber
      versionNumber
      quotationStage
      clientStatus
      listTitle
      listTotal
    }
  }
`;

const FIND_QUOTE_VERSION = `
  query FindQuoteVersionForContract($id: String!) {
    findOneQuotationVersion(id: $id) {
      id
      quotationNumber
      versionNumber
      quotationStage
      clientStatus
      listTitle
      listTotal
      clientDescription
      rawData
    }
  }
`;

const FREQUENCY_OPTIONS = [
  'One-off',
  'Daily',
  'Weekly',
  'Fortnightly',
  'Monthly',
];

function buildEmptyForm(contractNumber) {
  return {
    id: null,
    contractNumber: contractNumber || '',
    bookingId: '',
    quotationNumber: '',
    quotationVersionId: '',
    contractStatus: 'Draft',
    contractDate: toDateInputValue(new Date()),
    title: '',
    startDate: '',
    endDate: '',
    validUntil: '',
    client: '',
    email: '',
    companyName: '',
    siteAddress: '',
    contactPerson: '',
    billingAddress: '',
    phoneNumber: '',
    customerType: '',
    serviceType: '',
    frequency: '',
    siteSize: '',
    staffRequired: '',
    totalContracted: '',
    gstAmount: '',
    additionalCharges: '',
    discount: '',
    paymentTerms: 'Net 30',
    paymentReceived: '',
    scopeOfWork: '',
    services: [],
    termRows: [],
    terms: '',
    notes: '',
    file: null,
  };
}

function toDateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function moneyValue(value) {
  const num = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(num) ? num : 0;
}

/** GST is 10% of contract value + charges − discount, stored as dollars. */
function withGst(prev, patch) {
  const next = { ...prev, ...patch };
  const base =
    moneyValue(next.totalContracted) +
    moneyValue(next.additionalCharges) -
    moneyValue(next.discount);
  next.gstAmount = (Math.round(Math.max(0, base) * 10) / 100).toFixed(2);
  return next;
}

function leadLabel(lead) {
  if (!lead) return '';
  const name = String(lead.fullName || '').trim() || 'Untitled lead';
  const email = String(lead.email || '').trim();
  return email ? `${name} — ${email}` : name;
}

function matchFrequency(value) {
  const raw = String(value || '')
    .split('|')[0]
    .trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower === 'one-time' || lower === 'one time' || lower === 'onetime') {
    return 'One-off';
  }
  return (
    FREQUENCY_OPTIONS.find((option) => option.toLowerCase() === lower) || raw
  );
}

function quoteVersionLabel(row) {
  const version = Number(row.versionNumber) || 1;
  const stage = String(row.quotationStage || '').toUpperCase();
  const client = String(row.clientStatus || '').toUpperCase();
  const bits = [`v${version}`];
  if (client === 'ACCEPTED') bits.push('Accepted');
  else if (client === 'REJECTED') bits.push('Rejected');
  else if (stage === 'DRAFT') bits.push('Draft');
  else if (stage === 'SENT') bits.push('Sent');
  return bits.join(' — ');
}

function termsFromServices(services) {
  return (Array.isArray(services) ? services : []).reduce(
    (rows, line) => mergeTermRows(rows, line.sourceTerms || []),
    [],
  );
}

function isAcceptedQuote(row) {
  return String(row?.clientStatus || '').toUpperCase() === 'ACCEPTED';
}

function pickDefaultVersion(rows) {
  const accepted = rows.find(isAcceptedQuote);
  return accepted?.id || '';
}

function commercialFromQuote(quote) {
  const raw = quote?.rawData && typeof quote.rawData === 'object' ? quote.rawData : {};
  const workflow = raw.workflow && typeof raw.workflow === 'object' ? raw.workflow : {};
  const services = raw.services && typeof raw.services === 'object' ? raw.services : {};
  const lines = Array.isArray(workflow.serviceLines)
    ? workflow.serviceLines
    : Array.isArray(services.data)
      ? services.data
      : [];
  const firstLine = lines[0] || {};
  const firstService =
    firstLine.service?.service ||
    firstLine.serviceName ||
    firstLine.catalogName ||
    quote.listTitle ||
    '';
  const subtotal = moneyValue(
    services.totalPrice ?? workflow.subtotal ?? quote.listTotal,
  );
  const tax = moneyValue(services.taxAmount ?? workflow.taxAmount);
  const bookingInfo =
    raw.bookingInfo && typeof raw.bookingInfo === 'object' ? raw.bookingInfo : {};

  return {
    title: String(workflow.quotationTitle || raw.projectName || firstService || '').trim(),
    serviceType: String(firstService || '').trim(),
    frequency: matchFrequency(workflow.projectFrequency || firstLine.frequency),
    startDate: toDateInputValue(workflow.projectStartDate),
    validUntil: toDateInputValue(workflow.validUntil),
    totalContracted: subtotal ? String(subtotal) : '',
    gstAmount: tax ? String(tax) : '',
    companyName: String(bookingInfo.businessName || bookingInfo.companyName || '').trim(),
    quoteScope: String(
      quote.clientDescription || raw.clientDescription || '',
    ).trim(),
    quoteTerms: String(workflow.clientTerms || raw.clientTerms || '').trim(),
  };
}

function toComparableText(value) {
  return String(value || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function isCopiedFromQuote(contractValue, quoteValue) {
  const saved = toComparableText(contractValue);
  const quoted = toComparableText(quoteValue);
  if (!saved) return true;
  if (!quoted) return false;
  if (saved === quoted) return true;
  if (quoted.length >= 40 && saved.includes(quoted)) return true;
  if (saved.length >= 40 && quoted.includes(saved)) return true;
  return false;
}

function clientFromLead(lead) {
  const name = String(lead?.fullName || '').trim();
  const address = String(lead?.address || '').trim();
  return {
    client: name,
    email: String(lead?.email || '').trim(),
    phoneNumber: String(lead?.phone || '').trim(),
    siteAddress: address,
    billingAddress: address,
    contactPerson: name,
    frequency: matchFrequency(lead?.bookingFrequency),
  };
}

function normalizeLinkUrl(raw) {
  const url = String(raw || '').trim();
  if (!url) return '';
  if (/^(https?:\/\/|mailto:)/i.test(url)) return url;
  return `https://${url}`;
}

function RichTextEditor({ name, value, onChange, placeholder, disabled }) {
  const editorRef = useRef(null);
  const lastEmitted = useRef(value || '');
  const focusedRef = useRef(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const next = value || '';
    if (focusedRef.current) return;
    if (editor.innerHTML === next) {
      lastEmitted.current = next;
      return;
    }
    editor.innerHTML = next;
    editor.dataset.empty = next ? 'false' : 'true';
    lastEmitted.current = next;
  }, [value]);

  const emitHtml = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const html =
      editor.innerHTML === '<br>' || editor.innerHTML === '<div><br></div>'
        ? ''
        : editor.innerHTML;
    editor.dataset.empty = html ? 'false' : 'true';
    lastEmitted.current = html;
    onChange({ target: { name, value: html } });
  };

  const applyFormat = (command, arg) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, arg ?? null);
    emitHtml();
  };

  const insertLink = () => {
    if (disabled) return;
    const url = normalizeLinkUrl(window.prompt('Enter the link URL'));
    if (!url) return;
    applyFormat('createLink', url);
  };

  return (
    <div className="contract-form-editor">
      <div className="contract-form-editor-toolbar" role="toolbar" aria-label="Text formatting">
        <button
          type="button"
          title="Bold"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyFormat('bold')}
        >
          B
        </button>
        <button
          type="button"
          title="Italic"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyFormat('italic')}
        >
          I
        </button>
        <button
          type="button"
          title="Underline"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyFormat('underline')}
        >
          U
        </button>
        <button
          type="button"
          title="Align left"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyFormat('justifyLeft')}
        >
          <FiAlignLeft size={14} />
        </button>
        <button
          type="button"
          title="Add link"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={insertLink}
        >
          <FiLink size={14} />
        </button>
      </div>
      <div
        ref={editorRef}
        className="contract-form-rich-input"
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        data-empty={value ? 'false' : 'true'}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onInput={emitHtml}
        onBlur={() => {
          focusedRef.current = false;
          emitHtml();
        }}
      />
    </div>
  );
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="contract-form-section-head">
      <Icon size={16} />
      <span>{title}</span>
    </div>
  );
}

function FieldLabel({ children, required }) {
  return (
    <span className="contract-form-label">
      {children}
      {required ? <span className="contract-form-required">*</span> : null}
    </span>
  );
}

const ContractForm = ({
  onClose,
  onSubmit,
  editData,
  nextContractNumber,
  readOnly = false,
  /** New contract from a quotation: { bookingId, quotationNumber, quotationVersionId }. */
  prefill = null,
}) => {
  const [leads, setLeads] = useState([]);
  const [leadQuery, setLeadQuery] = useState('');
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [quotations, setQuotations] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [formData, setFormData] = useState(() => buildEmptyForm(nextContractNumber));
  const [submitAction, setSubmitAction] = useState(null);
  const fileInputRef = useRef(null);
  const skipQuoteCommercialFill = useRef(Boolean(editData));
  const userEditedNarrative = useRef({ scopeOfWork: false, terms: false });
  const editDataRef = useRef(editData);
  editDataRef.current = editData;
  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;
  const prefillClientFor = useRef(null);

  const loadLeads = useCallback(async () => {
    try {
      setLoadingLeads(true);
      const res = await graphqlRequest(FIND_LEADS, { page: 1, limit: 500 });
      setLeads(Array.isArray(res?.findAllBooking?.data) ? res.findAllBooking.data : []);
    } catch (err) {
      console.error('Failed to load leads for contract form:', err);
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    if (editData) return;
    const seed = prefillRef.current;
    setFormData({
      ...buildEmptyForm(nextContractNumber),
      ...(seed?.bookingId
        ? {
            bookingId: seed.bookingId,
            quotationNumber: seed.quotationNumber || '',
            quotationVersionId: seed.quotationVersionId || '',
          }
        : {}),
    });
    skipQuoteCommercialFill.current = false;
    userEditedNarrative.current = { scopeOfWork: false, terms: false };
  }, [editData, nextContractNumber]);

  useEffect(() => {
    const data = editDataRef.current;
    if (!data) return;
    skipQuoteCommercialFill.current = true;
    userEditedNarrative.current = { scopeOfWork: false, terms: false };
    const linkedQuote = Boolean(data.quotationVersionId);
    setFormData({
      id: data.id || null,
      contractNumber: data.contractId || '',
      bookingId: data.bookingId || '',
      quotationNumber: data.quotationNumber || '',
      quotationVersionId: data.quotationVersionId || '',
      contractStatus: data.status || 'Draft',
      contractDate: toDateInputValue(data.contractDate || data.billDate),
      title: data.title || data.service || '',
      startDate: toDateInputValue(data.startDate || data.billDate),
      endDate: toDateInputValue(data.endDate || data.dueDate),
      validUntil: toDateInputValue(data.validUntil || data.dueDate),
      client: data.client || '',
      email: data.clientEmail || '',
      companyName: data.companyName || '',
      siteAddress: data.siteAddress || '',
      contactPerson: data.contactPerson || '',
      billingAddress: data.billingAddress || '',
      phoneNumber: data.phoneNumber || '',
      customerType: data.customerType || '',
      serviceType: data.serviceType || data.service || '',
      frequency: data.frequency || '',
      siteSize: data.siteSize || '',
      staffRequired: data.staffRequired || '',
      totalContracted:
        data.serviceCost != null && data.serviceCost !== ''
          ? String(data.serviceCost)
          : data.total != null
            ? String(data.total)
            : '',
      gstAmount:
        data.gstAmount != null && data.gstAmount !== ''
          ? String(data.gstAmount)
          : '',
      additionalCharges:
        data.additionalCharges != null && data.additionalCharges !== ''
          ? String(data.additionalCharges)
          : '',
      discount:
        data.discount != null && data.discount !== ''
          ? String(data.discount)
          : '',
      paymentTerms: data.paymentTerms || 'Net 30',
      paymentReceived:
        data.received != null ? String(data.received) : '',
      scopeOfWork: linkedQuote ? '' : data.scopeOfWork || '',
      services: Array.isArray(data.services) ? data.services : [],
      termRows: (() => {
        const services = Array.isArray(data.services) ? data.services : [];
        const fromServices = termsFromServices(services);
        if (fromServices.length) return fromServices;
        if (linkedQuote || !data.terms || /<[a-z]/i.test(data.terms)) return [];
        return rowsFromProposalTerms(data.terms) || rowsFromClientTerms(data.terms);
      })(),
      terms: linkedQuote ? '' : data.terms || '',
      notes: data.notes || '',
      file: data.file || null,
    });

    if (!linkedQuote) return undefined;

    let cancelled = false;
    const savedScope = data.scopeOfWork || '';
    const savedTerms = data.terms || '';
    const contractId = data.id;
    graphqlRequest(FIND_QUOTE_VERSION, { id: data.quotationVersionId })
      .then((res) => {
        if (cancelled) return;
        const commercial = commercialFromQuote(res?.findOneQuotationVersion);
        setFormData((prev) => {
          if (prev.id !== contractId) return prev;
          return {
            ...prev,
            scopeOfWork: userEditedNarrative.current.scopeOfWork
              ? prev.scopeOfWork
              : isCopiedFromQuote(savedScope, commercial.quoteScope)
                ? ''
                : savedScope,
            terms: userEditedNarrative.current.terms
              ? prev.terms
              : isCopiedFromQuote(savedTerms, commercial.quoteTerms)
                ? ''
                : savedTerms,
          };
        });
      })
      .catch(() => {
        // Leave Scope / Terms empty so quotation wording is never restored.
      });
    return () => {
      cancelled = true;
    };
  }, [editData?.id]);

  useEffect(() => {
    const bookingId = formData.bookingId;
    if (!bookingId || loadingLeads) return;
    if (leads.some((row) => row.id === bookingId)) return;
    graphqlRequest(FIND_ONE_LEAD, { id: bookingId })
      .then((res) => {
        const lead = res?.findOneBooking;
        if (!lead?.id) return;
        setLeads((prev) =>
          prev.some((row) => row.id === lead.id) ? prev : [lead, ...prev],
        );
      })
      .catch((err) => {
        console.error('Failed to load selected lead:', err);
      });
  }, [formData.bookingId, leads, loadingLeads]);

  // Opened from a quotation: fill client details from its deal once the lead
  // is available (pricing, service and dates come from the quotation below).
  useEffect(() => {
    const seed = prefillRef.current;
    if (editData || !seed?.bookingId) return;
    if (formData.bookingId !== seed.bookingId) return;
    if (prefillClientFor.current === formData.contractNumber) return;
    const lead = leads.find((row) => row.id === seed.bookingId);
    if (!lead) return;
    prefillClientFor.current = formData.contractNumber;
    setFormData((prev) => {
      if (prev.bookingId !== seed.bookingId) return prev;
      const fromLead = clientFromLead(lead);
      return {
        ...prev,
        ...fromLead,
        frequency: prev.frequency || fromLead.frequency,
      };
    });
  }, [editData, leads, formData.bookingId, formData.contractNumber]);

  useEffect(() => {
    const bookingId = formData.bookingId;
    if (!bookingId) {
      setQuotations([]);
      setLoadingQuotes(false);
      return;
    }
    let cancelled = false;
    setLoadingQuotes(true);
    graphqlRequest(FIND_BOOKING_QUOTATIONS, { bookingId })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.findBookingQuotation)
          ? res.findBookingQuotation
          : [];
        setQuotations(rows);
        setFormData((prev) => {
          if (prev.bookingId !== bookingId) return prev;
          if (skipQuoteCommercialFill.current && prev.quotationVersionId) {
            return prev;
          }
          const numbers = [
            ...new Set(
              rows
                .map((row) => String(row.quotationNumber || '').trim())
                .filter(Boolean),
            ),
          ];
          const nextNumber =
            prev.quotationNumber && numbers.includes(prev.quotationNumber)
              ? prev.quotationNumber
              : numbers.length === 1
                ? numbers[0]
                : numbers.includes(prev.quotationNumber)
                  ? prev.quotationNumber
                  : prev.quotationNumber || numbers[0] || '';
          const versions = nextNumber
            ? rows.filter(
                (row) => String(row.quotationNumber || '') === nextNumber,
              )
            : rows;
          const nextVersionId =
            prev.quotationVersionId || pickDefaultVersion(versions);
          if (
            prev.quotationNumber === nextNumber &&
            prev.quotationVersionId === nextVersionId
          ) {
            return prev;
          }
          return {
            ...prev,
            quotationNumber: nextNumber,
            quotationVersionId: nextVersionId,
          };
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load quotations for lead:', err);
        setQuotations([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingQuotes(false);
      });
    return () => {
      cancelled = true;
    };
    // Only reload when the lead changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.bookingId]);

  useEffect(() => {
    if (!formData.quotationVersionId || formData.quotationNumber) return;
    const row = quotations.find((quote) => quote.id === formData.quotationVersionId);
    if (!row?.quotationNumber) return;
    setFormData((prev) =>
      prev.quotationNumber
        ? prev
        : { ...prev, quotationNumber: row.quotationNumber },
    );
  }, [formData.quotationNumber, formData.quotationVersionId, quotations]);

  useEffect(() => {
    const versionId = formData.quotationVersionId;
    if (!versionId || skipQuoteCommercialFill.current) return;
    let cancelled = false;
    graphqlRequest(FIND_QUOTE_VERSION, { id: versionId })
      .then((res) => {
        if (cancelled) return;
        const quote = res?.findOneQuotationVersion;
        if (!quote) return;
        const commercial = commercialFromQuote(quote);
        setFormData((prev) => {
          if (prev.quotationVersionId !== versionId) return prev;
          return {
            ...prev,
            quotationNumber: quote.quotationNumber || prev.quotationNumber,
            title: commercial.title || prev.title,
            serviceType: (prev.services || []).length
              ? prev.serviceType
              : commercial.serviceType || prev.serviceType,
            frequency: commercial.frequency || prev.frequency,
            startDate: commercial.startDate || prev.startDate,
            validUntil: commercial.validUntil || prev.validUntil,
            companyName: commercial.companyName || prev.companyName,
            scopeOfWork: userEditedNarrative.current.scopeOfWork
              ? prev.scopeOfWork
              : '',
            terms: userEditedNarrative.current.terms ? prev.terms : '',
          };
        });
      })
      .catch((err) => {
        console.error('Failed to load quotation version:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.quotationVersionId]);

  const acceptedQuotations = useMemo(() => {
    const accepted = quotations.filter(isAcceptedQuote);
    const currentId = String(formData.quotationVersionId || '').trim();
    if (
      currentId &&
      !accepted.some((row) => String(row.id) === currentId)
    ) {
      const current = quotations.find((row) => String(row.id) === currentId);
      if (current) return [current, ...accepted];
    }
    return accepted;
  }, [quotations, formData.quotationVersionId]);

  const quotationNumbers = useMemo(() => {
    const unique = [];
    const seen = new Set();
    for (const row of acceptedQuotations) {
      const number = String(row.quotationNumber || '').trim();
      if (!number || seen.has(number)) continue;
      seen.add(number);
      unique.push(number);
    }
    return unique;
  }, [acceptedQuotations]);

  const quotationVersions = useMemo(() => {
    const wanted = String(formData.quotationNumber || '').trim();
    const rows = wanted
      ? acceptedQuotations.filter(
          (row) => String(row.quotationNumber || '') === wanted,
        )
      : acceptedQuotations;
    return [...rows].sort(
      (a, b) => (Number(b.versionNumber) || 1) - (Number(a.versionNumber) || 1),
    );
  }, [formData.quotationNumber, acceptedQuotations]);

  const isSignedSource = ['Active', 'Completed', 'Terminated'].includes(
    String(editData?.status || ''),
  );

  const filteredLeads = useMemo(() => {
    const term = leadQuery.trim().toLowerCase();
    const rows = !term
      ? leads
      : leads.filter((lead) =>
          `${lead.fullName || ''} ${lead.email || ''} ${lead.phone || ''}`
            .toLowerCase()
            .includes(term),
        );
    if (formData.bookingId && !rows.some((lead) => lead.id === formData.bookingId)) {
      const selected = leads.find((lead) => lead.id === formData.bookingId);
      if (selected) return [selected, ...rows];
    }
    return rows;
  }, [formData.bookingId, leadQuery, leads]);

  const totalContractValue = useMemo(() => {
    const total =
      moneyValue(formData.totalContracted) +
      moneyValue(formData.gstAmount) +
      moneyValue(formData.additionalCharges) -
      moneyValue(formData.discount);
    return Math.max(0, total);
  }, [
    formData.totalContracted,
    formData.gstAmount,
    formData.additionalCharges,
    formData.discount,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'scopeOfWork' || name === 'terms') {
      userEditedNarrative.current[name] = true;
    }
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'totalContracted' || name === 'additionalCharges' || name === 'discount') {
        return withGst(prev, { [name]: value });
      }
      return next;
    });
  };

  const handleLeadChange = (e) => {
    const bookingId = e.target.value;
    skipQuoteCommercialFill.current = false;
    userEditedNarrative.current = { scopeOfWork: false, terms: false };
    const lead = leads.find((row) => row.id === bookingId);
    if (!lead) {
      setFormData((prev) => ({
        ...prev,
        bookingId: '',
        quotationNumber: '',
        quotationVersionId: '',
        client: '',
        email: '',
        phoneNumber: '',
        siteAddress: '',
        billingAddress: '',
        contactPerson: '',
        scopeOfWork: '',
        services: [],
        termRows: [],
        terms: '',
      }));
      setQuotations([]);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      bookingId,
      quotationNumber: '',
      quotationVersionId: '',
      scopeOfWork: '',
      services: [],
      termRows: [],
      terms: '',
      ...clientFromLead(lead),
    }));
  };

  const handleQuotationNumberChange = (e) => {
    const quotationNumber = e.target.value;
    skipQuoteCommercialFill.current = false;
    userEditedNarrative.current = { scopeOfWork: false, terms: false };
    const versions = quotations.filter(
      (row) => String(row.quotationNumber || '') === quotationNumber,
    );
    setFormData((prev) => ({
      ...prev,
      quotationNumber,
      quotationVersionId: pickDefaultVersion(versions),
      scopeOfWork: '',
      services: [],
      termRows: [],
      terms: '',
    }));
  };

  const handleQuotationVersionChange = (e) => {
    skipQuoteCommercialFill.current = false;
    userEditedNarrative.current = { scopeOfWork: false, terms: false };
    setFormData((prev) => ({
      ...prev,
      quotationVersionId: e.target.value,
      scopeOfWork: '',
      services: [],
      termRows: [],
      terms: '',
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      file: e.target.files?.[0] || null,
    }));
  };

  const submitContract = async (e, { status, sendEmail, action }) => {
    e.preventDefault();
    e.stopPropagation();
    if (submitAction) return;

    const nextStatus = status || formData.contractStatus || 'Draft';

    if (!String(formData.bookingId || '').trim()) {
      toast.error('Please select a lead.');
      return;
    }
    if (!String(formData.quotationVersionId || '').trim()) {
      toast.error(
        quotations.some(isAcceptedQuote)
          ? 'Select the accepted quotation version for this lead.'
          : 'This lead has no accepted quotation. Accept a quote first.',
      );
      return;
    }
    if (sendEmail && !String(formData.email || '').trim()) {
      toast.error(
        'Please enter the client email address before sending for signature.',
      );
      return;
    }
    if (
      !String(formData.title || '').trim() ||
      !String(formData.client || '').trim()
    ) {
      toast.error('Please fill in contract title and client name.');
      return;
    }

    const next = {
      ...formData,
      contractStatus: nextStatus,
      status: nextStatus,
      sendEmail: Boolean(sendEmail),
      serviceCost: formData.totalContracted,
      totalValue: totalContractValue,
      totalContracted: formData.totalContracted,
      due: totalContractValue - moneyValue(formData.paymentReceived),
    };

    setSubmitAction(action);
    try {
      await onSubmit?.(next);
      onClose?.();
    } catch (err) {
      console.error('Contract submit failed:', err);
      toast.error(err?.message || 'Failed to save contract.');
    } finally {
      setSubmitAction(null);
    }
  };

  const handleSave = (e) =>
    submitContract(e, {
      status: formData.contractStatus || 'Draft',
      sendEmail: false,
      action: 'save',
    });

  const handleSendForSignature = (e) =>
    submitContract(e, {
      status: 'Pending Signature',
      sendEmail: true,
      action: 'send',
    });

  const isBusy = Boolean(submitAction);
  const quotesDisabled = !formData.bookingId || loadingQuotes;

  return (
    <div className="contract-form-overlay" onClick={onClose} role="presentation">
      <div
        className="contract-form-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={
          readOnly
            ? 'View contract'
            : editData
              ? 'Edit contract'
              : 'Add new contract'
        }
      >
        <header className="contract-form-header">
          <div className="contract-form-title-wrap">
            <FiFileText size={24} />
            <div>
              <h2>
                {readOnly
                  ? 'View Contract'
                  : editData
                    ? 'Edit Contract'
                    : 'Add New Contract'}
              </h2>
              <p>
                {readOnly
                  ? 'Review this contract.'
                  : 'Create, manage, and send client contracts.'}
              </p>
            </div>
          </div>
          <div className="contract-form-header-actions">
            <span className="contract-form-number-pill">
              {formData.contractNumber}
              {editData?.versionNumber
                ? ` · v${editData.versionNumber}`
                : ''}
            </span>
            <button type="button" className="contract-form-close" onClick={onClose}>
              <FiX size={24} />
            </button>
          </div>
        </header>

        <form
          className="contract-form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="contract-form-body">
            <fieldset
              disabled={readOnly}
              className="contract-form-fieldset"
            >
            <section className="contract-form-section">
              <SectionHeader icon={FiUsers} title="CLIENT DETAILS" />
              <div className="contract-form-grid">
                <label className="contract-form-field contract-form-field--full">
                  <FieldLabel required>Lead</FieldLabel>
                  <input
                    type="search"
                    value={leadQuery}
                    onChange={(e) => setLeadQuery(e.target.value)}
                    placeholder="Search leads by name, email or phone"
                    className="contract-form-input"
                    disabled={loadingLeads}
                  />
                  <select
                    name="bookingId"
                    value={formData.bookingId}
                    onChange={handleLeadChange}
                    className="contract-form-input"
                    disabled={loadingLeads}
                  >
                    <option value="">
                      {loadingLeads
                        ? 'Loading leads…'
                        : filteredLeads.length === 0
                          ? 'No leads found'
                          : 'Select a lead…'}
                    </option>
                    {filteredLeads.map((lead) => (
                      <option key={lead.id} value={lead.id}>
                        {leadLabel(lead)}
                      </option>
                    ))}
                  </select>
                  <span className="contract-form-hint">
                    Selecting a lead fills the client details below.
                  </span>
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Client Name</FieldLabel>
                  <input name="client" value={formData.client} onChange={handleInputChange} placeholder="Acme Corporation" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Email Address</FieldLabel>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="contact@acmecorp.com" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Company Name</FieldLabel>
                  <input name="companyName" value={formData.companyName} onChange={handleInputChange} placeholder="Acme Corp Pty Ltd" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Site Address</FieldLabel>
                  <input name="siteAddress" value={formData.siteAddress} onChange={handleInputChange} placeholder="123 Design St, Sydney NSW 2000" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Contact Person</FieldLabel>
                  <input name="contactPerson" value={formData.contactPerson} onChange={handleInputChange} placeholder="John Smith" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Billing Address</FieldLabel>
                  <input name="billingAddress" value={formData.billingAddress} onChange={handleInputChange} placeholder="PO Box 100, Sydney NSW 3000" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Phone Number</FieldLabel>
                  <input name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} placeholder="+61 400 122 408" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Customer Type</FieldLabel>
                  <select name="customerType" value={formData.customerType} onChange={handleInputChange} className="contract-form-input">
                    <option value="">Select type...</option>
                    <option>Residential</option>
                    <option>Commercial</option>
                    <option>Strata</option>
                    <option>Government</option>
                  </select>
                </label>
              </div>
            </section>

            <section className="contract-form-section">
              <SectionHeader icon={FiFileText} title="CONTRACT INFORMATION" />
              <div className="contract-form-grid">
                <label className="contract-form-field">
                  <FieldLabel required>Contract Number</FieldLabel>
                  <input
                    type="text"
                    name="contractNumber"
                    value={formData.contractNumber}
                    onChange={handleInputChange}
                    className="contract-form-input contract-form-input--readonly"
                    readOnly
                  />
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Quotation No.</FieldLabel>
                  <select
                    name="quotationNumber"
                    value={formData.quotationNumber}
                    onChange={handleQuotationNumberChange}
                    className="contract-form-input"
                    disabled={quotesDisabled}
                  >
                    <option value="">
                      {!formData.bookingId
                        ? 'Select a lead first'
                        : loadingQuotes
                          ? 'Loading quotations…'
                          : quotationNumbers.length === 0
                            ? 'No accepted quotation for this lead'
                            : 'Select quotation…'}
                    </option>
                    {quotationNumbers.map((number) => (
                      <option key={number} value={number}>
                        {number}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Quotation Version</FieldLabel>
                  <select
                    name="quotationVersionId"
                    value={formData.quotationVersionId}
                    onChange={handleQuotationVersionChange}
                    className="contract-form-input"
                    disabled={quotesDisabled || !formData.quotationNumber}
                  >
                    <option value="">
                      {!formData.quotationNumber
                        ? 'Select a quotation first'
                        : quotationVersions.length === 0
                          ? 'No accepted versions'
                          : 'Select accepted version…'}
                    </option>
                    {quotationVersions.map((row) => (
                      <option key={row.id} value={row.id}>
                        {quoteVersionLabel(row)}
                      </option>
                    ))}
                  </select>
                  <span className="contract-form-hint">
                    The contract must use the accepted quotation version.
                  </span>
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Contract Title</FieldLabel>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g. Quarterly Office Cleaning Contract"
                    className="contract-form-input"
                  />
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Contract Status</FieldLabel>
                  {isSignedSource || readOnly ? (
                    <>
                      <input
                        className="contract-form-input contract-form-input--readonly"
                        value={formData.contractStatus}
                        readOnly
                      />
                      {isSignedSource && !readOnly ? (
                        <span className="contract-form-hint">
                          This version is signed. Saving creates a new draft
                          version instead of changing the signed contract.
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <select
                      name="contractStatus"
                      value={
                        formData.contractStatus === 'Pending Signature'
                          ? 'Pending Signature'
                          : 'Draft'
                      }
                      onChange={handleInputChange}
                      className="contract-form-input"
                    >
                      <option value="Draft">Draft</option>
                      <option value="Pending Signature">Pending Signature</option>
                    </select>
                  )}
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Contract Date</FieldLabel>
                  <input
                    type="date"
                    name="contractDate"
                    value={formData.contractDate}
                    onChange={handleInputChange}
                    className="contract-form-input"
                  />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Valid Until</FieldLabel>
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleInputChange}
                    className="contract-form-input"
                  />
                  <span className="contract-form-hint">Date after which contract requires renewal</span>
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Start Date</FieldLabel>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="contract-form-input"
                  />
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>End Date</FieldLabel>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="contract-form-input"
                  />
                </label>
              </div>
            </section>

            <section className="contract-form-section">
              <SectionHeader icon={FiTool} title="SERVICE DETAILS" />
              <div className="contract-form-grid">
                <label className="contract-form-field">
                  <FieldLabel>Service Type</FieldLabel>
                  <input
                    value={formData.serviceType || ''}
                    readOnly
                    className="contract-form-input"
                    placeholder="Filled from the service you add"
                  />
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Frequency</FieldLabel>
                  <select name="frequency" value={formData.frequency} onChange={handleInputChange} className="contract-form-input">
                    <option value="">Select frequency...</option>
                    {formData.frequency &&
                    !FREQUENCY_OPTIONS.includes(formData.frequency) ? (
                      <option value={formData.frequency}>{formData.frequency}</option>
                    ) : null}
                    {FREQUENCY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Site Size</FieldLabel>
                  <input name="siteSize" value={formData.siteSize} onChange={handleInputChange} placeholder="e.g. 5000 sq ft" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Estimated Staff Required</FieldLabel>
                  <input type="number" name="staffRequired" value={formData.staffRequired} onChange={handleInputChange} placeholder="4" className="contract-form-input" />
                </label>
              </div>
              <div className="contract-form-field contract-form-field--full">
                <ProposalServices
                  lines={formData.services}
                  onAdd={(pick) => {
                    userEditedNarrative.current.terms = true;
                    setFormData((prev) => {
                      if ((prev.services || []).some((line) => line.catalogServiceId && line.catalogServiceId === pick.catalogId)) {
                        return prev;
                      }
                      const services = [...(prev.services || []), lineFromPick(pick)];
                      const termRows = mergeTermRows(prev.termRows, pick.termRows || []);
                      return withGst(prev, {
                        services,
                        termRows,
                        terms: formatProposalTerms(termRows),
                        serviceType: serviceTypeFromLines(services),
                        totalContracted: String(serviceTaskTotal(services)),
                      });
                    });
                  }}
                  onRemove={(index) => {
                    userEditedNarrative.current.terms = true;
                    setFormData((prev) => {
                      const removed = (prev.services || [])[index];
                      const services = (prev.services || []).filter((_, i) => i !== index);
                      const termRows = dropServiceTerms(prev.termRows, removed?.catalogServiceId);
                      return withGst(prev, {
                        services,
                        termRows,
                        terms: formatProposalTerms(termRows),
                        serviceType: serviceTypeFromLines(services),
                        totalContracted: services.length ? String(serviceTaskTotal(services)) : '0',
                      });
                    });
                  }}
                  onChange={(services) => {
                    const total = serviceTaskTotal(services);
                    setFormData((prev) =>
                      withGst(prev, {
                        services,
                        serviceType: serviceTypeFromLines(services),
                        totalContracted: (services || []).length ? String(total) : prev.totalContracted,
                      }),
                    );
                  }}
                />
              </div>
            </section>

            <section className="contract-form-section">
              <SectionHeader icon={FiBriefcase} title="CONTRACT PRICING & PAYMENT" />
              <div className="contract-form-grid">
                <label className="contract-form-field">
                  <FieldLabel required>Contract Value</FieldLabel>
                  <input type="number" name="totalContracted" value={formData.totalContracted} onChange={handleInputChange} placeholder="0.00" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>GST (10%)</FieldLabel>
                  <input
                    type="number"
                    name="gstAmount"
                    value={formData.gstAmount}
                    readOnly
                    placeholder="0.00"
                    className="contract-form-input"
                  />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Additional Charges</FieldLabel>
                  <input type="number" name="additionalCharges" value={formData.additionalCharges} onChange={handleInputChange} placeholder="0.00" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel>Discount</FieldLabel>
                  <input type="number" name="discount" value={formData.discount} onChange={handleInputChange} placeholder="0.00" className="contract-form-input" />
                </label>
                <label className="contract-form-field">
                  <FieldLabel required>Payment Terms</FieldLabel>
                  <select name="paymentTerms" value={formData.paymentTerms} onChange={handleInputChange} className="contract-form-input">
                    <option>Net 7</option>
                    <option>Net 14</option>
                    <option>Net 30</option>
                    <option>Due on receipt</option>
                  </select>
                </label>
                <div className="contract-form-field">
                  <FieldLabel>Total Contract Value</FieldLabel>
                  <div className="contract-form-total">
                    <span>AUTO-CALCULATED</span>
                    <strong>${totalContractValue.toFixed(2)}</strong>
                  </div>
                  <span className="contract-form-hint">Value + charges − discount, then 10% GST</span>
                </div>
              </div>
            </section>

            <section className="contract-form-section">
              <SectionHeader icon={FiCalendar} title="CONTRACT TERMS & SCOPE" />
              <div className="contract-form-field contract-form-field--full">
                <FieldLabel>Scope of Work</FieldLabel>
                <RichTextEditor
                  name="scopeOfWork"
                  value={formData.scopeOfWork}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder="Describe the cleaning scope, service inclusions, deliverables, and client requirements."
                />
              </div>
              <div className="contract-form-field contract-form-field--full">
                {(formData.services || []).length ? (
                  <QuoteTermsEditor
                    rows={formData.termRows || []}
                    onChange={(termRows) => {
                      userEditedNarrative.current.terms = true;
                      setFormData((prev) => ({
                        ...prev,
                        termRows,
                        terms: formatProposalTerms(termRows),
                      }));
                    }}
                  />
                ) : (
                  <>
                    <FieldLabel>Terms & Conditions</FieldLabel>
                    <RichTextEditor
                      name="terms"
                      value={formData.terms}
                      onChange={handleInputChange}
                      disabled={readOnly}
                      placeholder="Enter contract terms, service conditions, and any special clauses."
                    />
                  </>
                )}
              </div>
              <div className="contract-form-field contract-form-field--full">
                <FieldLabel>Special Clauses / Notes</FieldLabel>
                <RichTextEditor
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  disabled={readOnly}
                  placeholder="Any additional notes, exceptions, or special arrangements."
                />
              </div>
            </section>

            <section className="contract-form-section">
              <SectionHeader icon={FiPaperclip} title="CONTRACT FILES" />
              <div
                className="contract-form-upload"
                role="button"
                tabIndex={readOnly ? -1 : 0}
                onClick={() => {
                  if (readOnly) return;
                  fileInputRef.current?.click();
                }}
                onKeyDown={(e) => {
                  if (readOnly) return;
                  if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                }}
              >
                <div className="contract-form-upload-icon">
                  <FiUploadCloud size={28} />
                </div>
                <p>Drag & drop files here, or <span>Browse</span></p>
                <small>PDF, Images, Word Documents supported</small>
                <input ref={fileInputRef} type="file" onChange={handleFileChange} className="contract-form-file-input" accept=".pdf,.doc,.docx,image/*" />
              </div>
              {formData.file ? <p className="contract-form-file-name">{formData.file.name}</p> : null}
            </section>
            </fieldset>
          </div>

          <footer className="contract-form-footer">
            {readOnly ? <span /> : (
              <button type="button" className="contract-form-download">
                <FiDownload size={17} />
                Download PDF
              </button>
            )}
            <div className="contract-form-actions">
              <button
                type="button"
                className="contract-form-cancel"
                onClick={onClose}
                disabled={isBusy}
              >
                {readOnly ? 'Close' : 'Cancel'}
              </button>
              {readOnly ? null : (
                <>
                  <button
                    type="button"
                    className="contract-form-draft"
                    onClick={handleSave}
                    disabled={isBusy}
                  >
                    {submitAction === 'save' ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="contract-form-send"
                    onClick={handleSendForSignature}
                    disabled={isBusy}
                  >
                    {submitAction === 'send' ? 'Sending…' : 'Send for Signature'}
                    <FiEdit3 size={16} />
                  </button>
                </>
              )}
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default ContractForm;
